import React from 'react';
import { entitySelectLabel, getClientEntities, getEntityById } from './fundingClientEntities';

export const FUNDING_PRODUCT_CATEGORIES = [
  { label: 'Personal Loan', keys: ['Personal Loan'] },
  { label: 'Personal Credit Card', keys: ['Personal Credit Card', '0% Personal Credit Card'] },
  { label: '0% Business Credit Card', keys: ['0% Business Credit Card'] },
  { label: 'Non-Promo Business Credit Card', keys: ['Non-Promo Business Credit Card', 'No Promo Business Credit Card'] },
  { label: 'Business LOC', keys: ['Business Line of Credit', 'Business LOC'] },
  { label: 'Business Loan', keys: ['Business Loan', 'Term Loan', 'SBA Loan'] },
];

export const FUNDING_PRODUCT_CATEGORY_LABELS = FUNDING_PRODUCT_CATEGORIES.map((category) => category.label);

export function resolveFundingProductCategory(lendingProduct) {
  const product = String(lendingProduct || '').trim();
  if (!product) return null;
  const normalized = product.toLowerCase();
  for (const category of FUNDING_PRODUCT_CATEGORIES) {
    if (category.keys.some((key) => key.toLowerCase() === normalized)) {
      return category.label;
    }
  }
  return null;
}

export function sumApprovedByProductCategories(applications = []) {
  const totals = Object.fromEntries(FUNDING_PRODUCT_CATEGORY_LABELS.map((label) => [label, 0]));
  applications.forEach((app) => {
    if (app.decision !== 'Approved') return;
    const category = resolveFundingProductCategory(app.lendingProduct);
    if (!category) return;
    totals[category] += Number(app.approvalAmount) || 0;
  });
  return FUNDING_PRODUCT_CATEGORY_LABELS.map((label) => ({ label, value: totals[label] }));
}

export function computeFundingAmountCardTotals(applications = []) {
  const byLabel = Object.fromEntries(
    sumApprovedByProductCategories(applications).map((item) => [item.label, item.value]),
  );
  return {
    personalFundingAmount: (byLabel['Personal Loan'] || 0) + (byLabel['Personal Credit Card'] || 0),
    creditCardAmount: (byLabel['0% Business Credit Card'] || 0) + (byLabel['Non-Promo Business Credit Card'] || 0),
    businessFundingAmount: (byLabel['Business LOC'] || 0) + (byLabel['Business Loan'] || 0),
  };
}

export function resolveApplicationEntityLabel(app, client) {
  if (app?.entityLabel) return app.entityLabel;
  if (!app?.entityId) return app?.businessLegalName || '—';
  const entity = getEntityById(client, app.entityId);
  if (!entity) return app?.businessLegalName || app?.entityLabel || '—';
  return entity.businessLegalName || entity.dbaName || entitySelectLabel(entity);
}

const BUREAU_LABELS = {
  experian: 'Experian',
  transunion: 'TransUnion',
  equifax: 'Equifax',
};

export function formatBureauPullLabel(key) {
  if (!key) return '—';
  return BUREAU_LABELS[key] || key;
}

export function formatApplicationSource(source) {
  if (source === 'uploaded_scan') return 'Uploaded Scan';
  if (source === 'manual') return 'Manual';
  return source ? String(source) : 'Manual';
}

export function formatApplicantPills(application, client = null) {
  if (application?.applicantPills?.length) {
    return application.applicantPills.map((pill, i) => (
      <span key={`${pill.name}-${i}`} className="fg-applicant-pill">{pill.name} {pill.percent}%</span>
    ));
  }

  if (application?.applicants?.length) {
    const entities = client ? getClientEntities(client) : [];
    const entity = entities.find((e) => e.id === application.entityId);
    return application.applicants.map((app) => {
      const owner = entity?.owners?.find((o) => o.id === app.ownerId);
      const firstName = (owner?.name || application.ownerName || 'Owner').split(' ')[0];
      return (
        <span key={app.ownerId} className="fg-applicant-pill">
          {firstName} {app.ownershipPercentUsed}%
        </span>
      );
    });
  }

  if (application?.ownerName) {
    return <span className="fg-applicant-pill">{application.ownerName.split(' ')[0]} 100%</span>;
  }

  return <span className="fg-applicant-pill muted">—</span>;
}

export function buildApplicantPillsFromDraft(applicants = [], entity) {
  return applicants.map((app) => {
    const owner = entity?.owners?.find((o) => o.id === app.ownerId);
    const firstName = (owner?.name || 'Owner').split(' ')[0];
    return { name: firstName, percent: app.ownershipPercentUsed };
  });
}
